import { Flex } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  deleteQuestionnaire,
  fetchQuestionnaire,
  fetchQuestionnaires,
  getQuestionnaireScore,
  putQuestionnaire,
} from '@/apis/questionnaire';
import FilledButton from '@/components/FilledButton';
import OutlinedButton from '@/components/OutlinedButton';
import UserMenu from '@/components/UserMenu';
import { MessageType, Path, QuestionModule } from '@/enums';
import { useApiRequest, useAppFeedback } from '@/hooks';
import { QuestionnaireListItem } from '@/types';

import * as Style from './QuestionnaireSelection.style';

// 定義階段與模組的對應關係 (二維陣列：索引 = 階段序號)
const STAGE_MODULE_MAPPING = [
  [QuestionModule.Common, QuestionModule.Business], // 第一階段 (Stage.First)
  [QuestionModule.SDGs], // 第二階段 (Stage.Second)
] as const;

// 階段名稱對應索引的映射
const STAGE_INDEX_MAP = {
  first: 0,
  second: 1,
} as const;

type StageType = keyof typeof STAGE_INDEX_MAP;

export default function QuestionnaireSelection() {
  const [activeQuestionnaire, setActiveQuestionnaire] =
    useState<QuestionnaireListItem | null>(null);
  const [isResultReady, setIsResultReady] = useState(false);

  const navigate = useNavigate();

  const { showMessage, showConfirm } = useAppFeedback();

  // Use useApiRequest for loading questionnaires with auto-cancellation
  const {
    data: questionnairesResponse,
    loading,
    execute: loadQuestionnaires,
  } = useApiRequest(() => fetchQuestionnaires(), {
    immediate: true,
    requestKey: 'questionnaire-list',
    onSuccess: (response) => {
      // If no questionnaires, navigate to root
      if (!response.data || response.data.length === 0) {
        navigate(Path.Root);
      }
    },
    onError: (error) => {
      showMessage('載入問卷失敗', MessageType.Error);
      console.error('Failed to fetch questionnaires:', error);
    },
  });

  const questionnaires = questionnairesResponse?.data || [];

  // Use useApiRequest for fetching questionnaire detail with race condition prevention
  const { execute: fetchQuestionnaireDetail } = useApiRequest(
    (questionnaireId: string) => fetchQuestionnaire(questionnaireId, false),
    {
      requestKey: 'questionnaire-detail',
    },
  );

  const handleQuestionnaireSelect = (questionnaire: QuestionnaireListItem) => {
    setActiveQuestionnaire(questionnaire);
    setIsResultReady(false); // Reset result ready status
  };

  // Check if all 17 SDG descriptions are generated when a questionnaire is selected
  useEffect(() => {
    const checkResultReadiness = async () => {
      if (!activeQuestionnaire || !activeQuestionnaire.isCompleted) {
        setIsResultReady(false);
        return;
      }
      try {
        const response = await getQuestionnaireScore(
          activeQuestionnaire.id,
          false,
        );

        if (response?.data?.sdgScores) {
          // Check if all 17 SDG descriptions are generated
          const allDescriptionsReady = response.data.sdgScores.every(
            (score) => score.aiDescription && score.aiDescription.trim() !== '',
          );

          // Also check that we have exactly 17 SDGs
          const has17Sdgs = response.data.sdgScores.length === 17;

          setIsResultReady(allDescriptionsReady && has17Sdgs);
        } else {
          setIsResultReady(false);
        }
      } catch (error) {
        console.error('Failed to check result readiness:', error);
        setIsResultReady(false);
      }
    };

    checkResultReadiness();
  }, [activeQuestionnaire]);

  const isCurrentQuestionInStage = async (
    questionnaire: QuestionnaireListItem,
    stage: StageType,
  ): Promise<boolean> => {
    try {
      const response = await fetchQuestionnaireDetail(questionnaire.id);
      if (!response) return stage === 'first';

      const questionnaireData = response.data;

      if (
        !questionnaireData.currentQuestionId ||
        !questionnaireData.moduleQuestions
      ) {
        return stage === 'first'; // 如果沒有當前問題ID，預設在第一階段
      }

      const stageIndex = STAGE_INDEX_MAP[stage];
      const stageModules = STAGE_MODULE_MAPPING[stageIndex];

      // 檢查當前問題是否在指定階段的模組中
      for (const moduleGroup of questionnaireData.moduleQuestions) {
        const isStageModule = stageModules.some(
          (moduleName: string) => moduleName === moduleGroup.module.name,
        );
        if (isStageModule) {
          const questionExists = moduleGroup.questions.some(
            (question) => question.id === questionnaireData.currentQuestionId,
          );
          if (questionExists) {
            return true; // 當前問題在指定階段內
          }
        }
      }
      return false; // 當前問題不在指定階段內
    } catch (error) {
      console.error(
        `Error checking current question in ${stage} stage:`,
        error,
      );
      return stage === 'first'; // 錯誤時預設第一階段為 true
    }
  };

  const getStageFirstQuestionId = async (
    questionnaire: QuestionnaireListItem,
    stage: StageType,
  ): Promise<string | null> => {
    try {
      const response = await fetchQuestionnaireDetail(questionnaire.id);
      if (!response) return null;

      const questionnaireData = response.data;

      if (!questionnaireData.moduleQuestions) {
        return null;
      }

      const stageIndex = STAGE_INDEX_MAP[stage];
      const stageModules = STAGE_MODULE_MAPPING[stageIndex];

      // 按模組順序排序並找到指定階段的第一個問題
      const sortedModules = [...questionnaireData.moduleQuestions].sort(
        (a, b) => a.module.order - b.module.order,
      );

      for (const moduleGroup of sortedModules) {
        const isStageModule = stageModules.some(
          (moduleName) => moduleName === moduleGroup.module.name,
        );
        if (isStageModule) {
          if (moduleGroup.questions.length > 0) {
            return moduleGroup.questions[0]?.id || null;
          }
        }
      }
      return null;
    } catch (error) {
      console.error(`Error getting ${stage} stage first question:`, error);
      return null;
    }
  };

  const handleQuestionnaireNavigation = async (
    questionnaire: QuestionnaireListItem,
    targetStage: StageType = 'first',
  ) => {
    try {
      // 檢查當前問題是否已經在目標階段
      const isInTargetStage = await isCurrentQuestionInStage(
        questionnaire,
        targetStage,
      );

      if (!isInTargetStage) {
        // 如果當前問題不在目標階段，更新到目標階段的第一題
        const firstQuestionId = await getStageFirstQuestionId(
          questionnaire,
          targetStage,
        );
        if (firstQuestionId) {
          await putQuestionnaire(
            {
              questionnaireId: questionnaire.id,
              currentQuestionId: firstQuestionId,
            },
            false,
          );
        }
      }
      // 如果已經在目標階段，保持當前問題不變

      // 導航到問卷頁面
      navigate(
        `${Path.Questionnaire.replace(':questionnaireId', questionnaire.id)}`,
      );
    } catch (error) {
      showMessage('導航失敗', MessageType.Error);
      console.error('Failed to navigate to questionnaire:', error);
    }
  };

  const getModuleProgress = (
    questionnaire: QuestionnaireListItem,
    moduleName: QuestionModule,
  ) => {
    return questionnaire.moduleProgress?.find((m) => m.module === moduleName);
  };

  const getStageProgress = (
    questionnaire: QuestionnaireListItem,
    stage: StageType,
  ) => {
    const stageIndex = STAGE_INDEX_MAP[stage];
    const stageModules = STAGE_MODULE_MAPPING[stageIndex];
    const moduleProgressList = stageModules
      .map((moduleName) => getModuleProgress(questionnaire, moduleName))
      .filter(Boolean);

    if (moduleProgressList.length === 0) return null;

    const totalCompleted = moduleProgressList.reduce(
      (sum, progress) => sum + (progress?.completed || 0),
      0,
    );
    const totalQuestions = moduleProgressList.reduce(
      (sum, progress) => sum + (progress?.total || 0),
      0,
    );
    const isCompleted = totalCompleted === totalQuestions && totalQuestions > 0;

    return {
      completed: totalCompleted,
      total: totalQuestions,
      isCompleted,
      firstQuestionIndex: moduleProgressList[0]?.firstQuestionIndex || 0,
    };
  };

  const getSurveyStageProgress = (questionnaire: QuestionnaireListItem) => {
    return getStageProgress(questionnaire, 'first');
  };

  const handleDelete = async (
    questionnaireId: string,
    questionnaireName: string,
  ) => {
    showConfirm(
      '確認刪除',
      `確定要刪除問卷「${questionnaireName || '未命名問卷'}」嗎？此動作無法復原。`,
      async () => {
        try {
          await deleteQuestionnaire(questionnaireId);
          showMessage('問卷已成功刪除', MessageType.Success);
          // 重新載入問卷列表
          await loadQuestionnaires();
        } catch (error) {
          showMessage('刪除問卷失敗', MessageType.Error);
          console.error('Failed to delete questionnaire:', error);
        }
      },
      undefined,
      '刪除',
      '取消',
      'danger',
    );
  };

  if (loading) {
    return (
      <Style.Container>
        <Style.TitleText>載入中...</Style.TitleText>
      </Style.Container>
    );
  }

  return (
    <Style.Container>
      <UserMenu />
      <Flex vertical>
        <Style.TitleText>
          哈囉！
          {activeQuestionnaire
            ? `${questionnaires.find((q) => q.id === activeQuestionnaire.id)?.name || '未命名問卷'} `
            : ''}
          歡迎回來～
        </Style.TitleText>
        <Style.TitleText>
          今天你要前往
          {activeQuestionnaire ? '哪個問卷階段' : '哪一企業體的問卷'}呢？
        </Style.TitleText>
      </Flex>

      {activeQuestionnaire ? (
        <Style.ButtonContainer>
          <OutlinedButton
            height="4rem"
            width="100%"
            onClick={() =>
              handleQuestionnaireNavigation(activeQuestionnaire, 'first')
            }
          >
            第一階段：現況調查
          </OutlinedButton>

          <OutlinedButton
            height="4rem"
            width="100%"
            disabled={!getSurveyStageProgress(activeQuestionnaire)?.isCompleted}
            onClick={() =>
              handleQuestionnaireNavigation(activeQuestionnaire, 'second')
            }
          >
            第二階段：產業分類問題
          </OutlinedButton>

          <OutlinedButton
            height="4rem"
            width="100%"
            disabled={!activeQuestionnaire.isCompleted || !isResultReady}
            onClick={() =>
              navigate(`${Path.Result.replace(':id', activeQuestionnaire.id)}`)
            }
          >
            結果頁查看
          </OutlinedButton>
        </Style.ButtonContainer>
      ) : (
        <Style.ButtonContainer>
          {questionnaires.map((questionnaire) => (
            <Style.QuestionnaireItem key={questionnaire.id}>
              <OutlinedButton
                height="4rem"
                width="100%"
                onClick={() => handleQuestionnaireSelect(questionnaire)}
                className="questionnaire-item"
              >
                {questionnaire.name || '未命名問卷'}
              </OutlinedButton>

              <Style.QuestionnaireActionWrapper className="questionnaire-action-wrapper">
                <FilledButton
                  height="2.5rem"
                  width="5rem"
                  onClick={() =>
                    handleDelete(
                      questionnaire.id,
                      questionnaire.name || '未命名問卷',
                    )
                  }
                >
                  刪除
                </FilledButton>
              </Style.QuestionnaireActionWrapper>
            </Style.QuestionnaireItem>
          ))}
        </Style.ButtonContainer>
      )}
    </Style.Container>
  );
}
